import { Box, type Sx } from '@inflearn/ds-react';

interface GlareHoverProps {
  width?: string;
  height?: string;
  background?: string;
  borderRadius?: string;
  children?: React.ReactNode;
  glareColor?: string;
  glareOpacity?: number;
  glareAngle?: number;
  glareSize?: number;
  transitionDuration?: number;
  playOnce?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const GlareHover: React.FC<GlareHoverProps> = ({
  width = '500px',
  height = '500px',
  background = '#000',
  borderRadius = '10px',
  children,
  glareColor = '#ffffff',
  glareOpacity = 0.5,
  glareAngle = -45,
  glareSize = 250,
  transitionDuration = 650,
  playOnce = false,
  className = '',
  style = {},
}) => {
  const hex = glareColor.replace('#', '');
  let rgba = glareColor;

  if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    rgba = `rgba(${r}, ${g}, ${b}, ${glareOpacity})`;
  } else if (/^[0-9A-Fa-f]{3}$/.test(hex)) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    rgba = `rgba(${r}, ${g}, ${b}, ${glareOpacity})`;
  }

  const vars: React.CSSProperties & { [k: string]: string } = {
    '--gh-width': width,
    '--gh-height': height,
    '--gh-bg': background,
    '--gh-br': borderRadius,
    '--gh-angle': `${glareAngle}deg`,
    '--gh-duration': `${transitionDuration}ms`,
    '--gh-size': `${glareSize}%`,
    '--gh-rgba': rgba,
  };

  const baseSx: Sx = {
    width: 'var(--gh-width)',
    height: 'var(--gh-height)',
    background: 'var(--gh-bg)',
    borderRadius: 'var(--gh-br)',
    border: '1px solid var(--gh-border)',
    overflow: 'hidden',
    position: 'relative',
    display: 'grid',
    placeItems: 'center',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: `linear-gradient(var(--gh-angle), hsla(0, 0%, 0%, 0) 60%, var(--gh-rgba) 70%, hsla(0, 0%, 0%, 0), hsla(0, 0%, 0%, 0) 100%)`,
      transition: playOnce ? 'none' : 'var(--gh-duration) ease',
      backgroundSize: 'var(--gh-size) var(--gh-size), 100% 100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '-100% -100%, 0 0',
    },
    '&:hover': {
      cursor: 'pointer',
      '&::before': {
        transition: 'var(--gh-duration) ease',
        backgroundPosition: '100% 100%, 0 0',
      },
    },
  };

  return (
    <Box
      component="div"
      className={className}
      sx={baseSx}
      style={{ ...vars, ...style } as React.CSSProperties}
    >
      {children}
    </Box>
  );
};

export default GlareHover;
